import { API_BASE_URL } from './api';
import { getToken } from './auth';

export type VerificationSummary = {
  verification_id: number;
  user_id: number;
  name: string;
  email: string;
  role: string;
  status: string;
  verification_submitted_at: string;
};

export type VerificationDocument = {
  id: number;
  doc_type: string;
  doc_url: string;
  doc_filename: string;
  created_at: string;
};

export type VerificationDetail = {
  verification_id: number;
  status: string;
  application_data: Record<string, unknown>;
  submitted_at: string;
  decided_at: string | null;
  admin_notes: string | null;
  user_id: number;
  name: string;
  email: string;
  role: string;
  phone: string | null;
  documents: VerificationDocument[];
};

function authHeader(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function submitVerificationApplication(formData: FormData) {
  const res = await fetch(`${API_BASE_URL}/auth/verification/request`, {
    method: 'POST',
    headers: authHeader(),
    body: formData,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Could not submit your application (${res.status})`);
  }

  return res.json();
}

export async function getPendingVerifications(): Promise<VerificationSummary[]> {
  const res = await fetch(`${API_BASE_URL}/auth/verification/pending`, { headers: authHeader() });
  if (!res.ok) throw new Error('Could not load pending applications');
  const body = await res.json();
  return body.data;
}

export async function getVerificationDetail(id: number): Promise<VerificationDetail> {
  const res = await fetch(`${API_BASE_URL}/auth/verification/${id}`, { headers: authHeader() });
  if (!res.ok) throw new Error('Could not load application detail');
  const body = await res.json();
  return body.data;
}

export async function decideVerification(id: number, status: 'approved' | 'rejected') {
  const res = await fetch(`${API_BASE_URL}/auth/verification/${id}/decide`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ status }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Could not update the application (${res.status})`);
  }

  return res.json();
}

// Plain <img src> / <a href> can't attach the Authorization header this
// route requires, so fetch the file ourselves and hand back a blob URL.
export async function fetchDocumentBlobUrl(docUrl: string): Promise<string> {
  const res = await fetch(`${API_BASE_URL}${docUrl}`, { headers: authHeader() });
  if (!res.ok) throw new Error('Could not load document');
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
